import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Building2, ChevronDown, ChevronRight, ChevronsUpDown, FileText, History, Layers } from 'lucide-react';
import { TablePagination } from '../table/SortableHeader';
import { PAGE_SIZE_OPTIONS } from './lmApi';
import { Checkbox, DECOR_TEXT, FOCUS_RING, LmTip, Pill, StateMessage, toneClasses } from './LmBits';
import CustomFieldBadge from '../custom-fields/CustomFieldBadge';
import SourceColumnBadge from '../custom-fields/SourceColumnBadge';
import { isBlankCustomValue, isTruthyCheckboxValue } from '../custom-fields/fieldTypes';
import { ISSUES_HELP, SOURCE_META, TAG_HELP, attentionOf, displayName, formatDate, matchHelp, matchOf, statusHelp, statusOf } from './lmColumns';
import { stepUpBlockedNotice, stepUpThenReload, STEP_UP_ACTION_LABEL } from '../../lib/stepUp';

/**
 * The shared List Management table (#770 LM-02).
 *
 * Selection semantics (documented in docs/runtime/current/list-management.md):
 * - a row checkbox toggles that row's stable `row_id`;
 * - the header checkbox toggles every SELECTABLE row on the current page
 *   (locked Imports rows are excluded);
 * - selection persists across pages until the tab/source/filters/search change;
 * - "select all N matching" is offered by the page shell, not here.
 *
 * Layout (#770 QA): only this component's table region scrolls, in both
 * directions, under a sticky header row; the selection checkbox and the
 * record-name column are pinned left, and the pagination bar stays in view.
 * The parent must hand it a bounded height (a `flex-1 min-h-0` column).
 */
export const SOURCE_ICONS = { Building2, FileText, History, Layers };

// Pinned columns: the checkbox column is exactly CHECK_COL wide, so the pinned
// name column starts where it ends. Sticky cells need an opaque background in
// BOTH themes: only solid tokens (bg-card, bg-background, bg-muted, bg-accent) go on
// them, never an alpha such as bg-muted/50.
const CHECK_COL = 'w-[42px] min-w-[42px] max-w-[42px]';
const PIN_CHECK = 'sticky left-0';
const PIN_NAME = 'sticky left-[42px] shadow-[1px_0_0_hsl(var(--border))]';
const HEAD_CELL = 'sticky top-0 bg-background border-b border-border';
const ATTENTION_EDGE = { review: 'shadow-[inset_3px_0_0_var(--warn)]', error: 'shadow-[inset_3px_0_0_var(--bad)]' };

function SortHeader({ column, sort, order, onSort, pinned }) {
    const active = column.sort && sort === column.sort;
    const ariaSort = active ? (order === 'asc' ? 'ascending' : 'descending') : 'none';
    const Arrow = !active ? ChevronsUpDown : order === 'asc' ? ArrowUp : ArrowDown;
    // #975: the sort button (or, on an unsortable column, the focusable label) is
    // the tooltip trigger, so click, Enter and aria-sort are unchanged. The
    // Custom / From file badges are their own triggers. side="bottom" opens into
    // the table rather than over the toolbar above the sticky header.
    return (
        <th scope="col" aria-sort={column.sort ? ariaSort : undefined}
            className={`px-3.5 py-2.5 text-left align-middle ${HEAD_CELL} ${pinned ? `${PIN_NAME} z-30` : 'z-20'}`}>
            <span className="inline-flex items-center gap-1.5">
                <LmTip description={column.description} side="bottom">
                    {column.sort ? (
                        <button type="button" onClick={() => onSort(column.sort)} data-testid={`lm-header-${column.id}`}
                                className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-ink-2 tracking-[0.01em] whitespace-nowrap hover:text-foreground ${FOCUS_RING}`}>
                            {column.label}
                            <Arrow className={`w-3 h-3 ${active ? 'text-ink-2' : DECOR_TEXT}`} aria-hidden="true" />
                        </button>
                    ) : (
                        <span tabIndex={column.description ? 0 : undefined} data-testid={`lm-header-${column.id}`}
                              className={`text-[11.5px] font-semibold text-ink-2 tracking-[0.01em] whitespace-nowrap ${FOCUS_RING}`}>
                            {column.label}
                        </span>
                    )}
                </LmTip>
                {column.isCustom && <LmTip description={TAG_HELP.custom} side="bottom"><CustomFieldBadge tabIndex={0} className={FOCUS_RING} /></LmTip>}
                {column.isFile && <LmTip description={TAG_HELP.file} side="bottom"><SourceColumnBadge tabIndex={0} className={FOCUS_RING} /></LmTip>}
            </span>
        </th>
    );
}

function Cell({ column, row, tab, onOpen }) {
    if (column.kind === 'status') return <Pill label={statusOf(row, tab)} description={statusHelp(row, tab)} testId="lm-badge-status" />;
    if (column.kind === 'match') {
        const m = matchHelp(row);
        return <Pill label={matchOf(row)} description={m.description} detail={m.detail} testId="lm-badge-match" />;
    }
    if (column.kind === 'issues') {
        const errs = row.validation?.errors || [];
        // #976: the only warning the column surfaces is company_missing (decision D1);
        // every other warning stays drawer-only. #975: both pills explain themselves in
        // the shared tooltip, which lists only the row's own messages.
        const noCompany = (row.validation?.warnings || []).find((w) => w.code === 'company_missing');
        if (!errs.length && !noCompany) return <span className={DECOR_TEXT}>—</span>;
        return (
            <span className="inline-flex gap-1">
                {errs.length > 0 && <Pill label={[`${errs.length} issue${errs.length > 1 ? 's' : ''}`, { tone: 'warn' }]}
                                          description={ISSUES_HELP} detail={errs.map((e) => e.message)}
                                          testId="lm-badge-issues" />}
                {noCompany && <span data-testid="lm-issue-no-company">
                    <Pill label={['No company', { tone: 'neutral' }]} description={TAG_HELP.no_company}
                          detail={[noCompany.message]} testId="lm-badge-no-company" />
                </span>}
            </span>
        );
    }
    if (column.kind === 'entity') return <span className="text-ink-2">{row.entity_type || '—'}</span>;
    const value = column.get ? column.get(row) : '';
    if (column.primary) {
        const label = value || displayName(row);
        return (
            <button type="button" onClick={(e) => { e.stopPropagation(); onOpen?.(row); }}
                    className="font-medium text-foreground hover:underline underline-offset-2 text-left"
                    aria-label={`Open ${displayName(row)}`} data-testid="lm-row-open">
                {label}
            </button>
        );
    }
    if (column.id === 'owner' && !row.owner_id) return <span className="text-muted-foreground">Unassigned</span>;
    // 0 and false are values (#972), so only null / undefined / '' / [] read as empty.
    return <>{isBlankCustomValue(value) ? <span className={DECOR_TEXT}>—</span> : cellText(column, value)}</>;
}

// A Checkbox custom field reads Yes / No, and older imports' text ("false", "no", "0", "n") is No.
const cellText = (column, value) => (column.fieldType === 'checkbox' ? (isTruthyCheckboxValue(value) ? 'Yes' : 'No') : String(value));

// The text a plain cell shows, for its full-value tooltip: cells are capped at
// 320px and truncate. Pills, the name button and "Unassigned" carry their own.
function plainCellText(column, row) {
    if (column.kind || column.primary || !column.get) return undefined;
    if (column.id === 'owner' && !row.owner_id) return undefined;
    const value = column.get(row);
    return isBlankCustomValue(value) ? undefined : cellText(column, value);
}

function GroupHeader({ batch, count, span, open, onToggle, allOn, someOn, onToggleSel, selectable }) {
    const meta = SOURCE_META[batch?.source_type] || SOURCE_META.all;
    const Icon = SOURCE_ICONS[meta.icon] || Layers;
    const when = batch?.confirmed_at || batch?.created_at;
    return (
        <tr className="bg-background">
            <td className={`py-2.5 pl-4 ${CHECK_COL} ${PIN_CHECK} z-10 bg-background border-y border-border`}>
                <Checkbox checked={allOn} indeterminate={someOn} onChange={onToggleSel} disabled={!selectable}
                          label={`Select all rows in ${batch?.batch_name || 'this batch'} on this page`} />
            </td>
            <td colSpan={span} className="px-3.5 py-2.5 border-y border-border">
                <button type="button" onClick={onToggle} aria-expanded={open}
                        className="sticky left-3.5 inline-flex items-center gap-2.5">
                    {open ? <ChevronDown className="w-[15px] h-[15px] text-muted-foreground" /> : <ChevronRight className="w-[15px] h-[15px] text-muted-foreground" />}
                    <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${toneClasses(meta)}`}>
                        <Icon className="w-[11px] h-[11px]" aria-hidden="true" />
                    </span>
                    <span className="text-[13px] font-semibold text-foreground whitespace-nowrap">{batch?.batch_name || 'Import'}</span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {batch?.source_type === 'pitchbook' ? `Synced ${formatDate(when)}` : [batch?.file_name, formatDate(when)].filter(Boolean).join(' · ')}
                    </span>
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-accent text-ink-2 text-[11px] font-semibold tabular-nums">{count}</span>
                    {batch?.import_id && (
                        <span className="text-[11.5px] font-mono text-muted-foreground whitespace-nowrap" title="Import ID" data-testid="lm-batch-import-id">
                            {batch.import_id}
                        </span>
                    )}
                </button>
            </td>
        </tr>
    );
}

export default function LmTable({
    tab, columns, rows, batches = {}, grouped = false, selected, onToggleRow, onToggleMany,
    sort, order, onSort, onOpenRow, loading, status, error, onRetry, emptyLabel,
    page, pageSize, total, onPageChange, onPageSizeChange,
}) {
    const [collapsed, setCollapsed] = useState({});
    const selectable = (row) => !row.locked;
    const pageSelectable = rows.filter(selectable);
    const onPage = pageSelectable.filter((r) => selected.has(r.row_id)).length;
    const allOn = pageSelectable.length > 0 && onPage === pageSelectable.length;
    const someOn = onPage > 0 && !allOn;

    const groups = useMemo(() => {
        if (!grouped) return [{ key: '_all', rows }];
        const order_ = [];
        const by = {};
        rows.forEach((r) => {
            if (!by[r.import_id]) { by[r.import_id] = []; order_.push(r.import_id); }
            by[r.import_id].push(r);
        });
        return order_.map((key) => ({ key, rows: by[key], batch: batches[key] }));
    }, [grouped, rows, batches]);

    const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 25)));

    let body;
    if (status === 'step_up') {
        // #854: refused for want of the code factor (the prompt was cancelled).
        // "Enter code" asks, then reloads the page so the summary, owners and
        // field labels refetch too — not just these rows.
        const notice = stepUpBlockedNotice();
        body = (
            <StateMessage kind="step_up"
                          title={notice.actionable ? 'Enter your authenticator code to use List Management' : 'List Management is not available in this session'}
                          detail={notice.text}
                          action={notice.actionable
                              ? <button type="button" onClick={() => stepUpThenReload()} className="text-[13px] font-medium text-violet-700 dark:text-violet-300 hover:underline">{STEP_UP_ACTION_LABEL}</button>
                              : null} />
        );
    } else if (status === 'unauthorized') {
        // Every other refusal; the server's own sentence says which one.
        body = <StateMessage kind="unauthorized" title="You don't have access to List Management" detail={error} />;
    } else if (status === 'unavailable') {
        body = <StateMessage kind="info" title="List Management is not available on your plan" />;
    } else if (status === 'error') {
        body = (
            <StateMessage kind="error" title="Could not load records" detail={error}
                          action={<button type="button" onClick={onRetry} className="text-[13px] font-medium text-violet-700 dark:text-violet-300 hover:underline">Try again</button>} />
        );
    } else if (!loading && rows.length === 0) {
        body = <StateMessage kind="empty" title={emptyLabel} />;
    }

    // The record-name column is pinned when it leads (it is never hideable).
    const pinFirst = Boolean(columns[0]?.primary);
    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 min-h-0 overflow-auto border-t border-border" aria-busy={loading || undefined}
                 data-testid="lm-table-scroll">
                <table className={`border-separate border-spacing-0 w-full ${columns.length > 8 ? 'min-w-[1600px]' : 'min-w-[900px]'}`} data-testid="lm-table">
                    <thead>
                        <tr>
                            <th scope="col" className={`py-2.5 pl-4 text-left ${CHECK_COL} ${HEAD_CELL} ${PIN_CHECK} z-30`}>
                                <Checkbox checked={allOn} indeterminate={someOn} disabled={!pageSelectable.length}
                                          onChange={() => onToggleMany(pageSelectable.map((r) => r.row_id), !allOn)}
                                          label="Select all selectable rows on this page" />
                            </th>
                            {columns.map((col, i) => (
                                <SortHeader key={col.id} column={col} sort={sort} order={order} onSort={onSort} pinned={pinFirst && i === 0} />
                            ))}
                        </tr>
                    </thead>
                    {!body && groups.map((g) => {
                        const open = !collapsed[g.key];
                        const gSel = g.rows.filter(selectable);
                        const n = gSel.filter((r) => selected.has(r.row_id)).length;
                        const gAll = gSel.length > 0 && n === gSel.length;
                        return (
                            <tbody key={g.key}>
                                {grouped && (
                                    <GroupHeader batch={g.batch} count={g.rows.length} span={columns.length} open={open}
                                                 onToggle={() => setCollapsed((c) => ({ ...c, [g.key]: open }))}
                                                 allOn={gAll} someOn={n > 0 && !gAll} selectable={gSel.length > 0}
                                                 onToggleSel={() => onToggleMany(gSel.map((r) => r.row_id), !gAll)} />
                                )}
                                {open && g.rows.map((row) => {
                                    const on = selected.has(row.row_id);
                                    const locked = !selectable(row);
                                    // Every cell paints the row background: pinned cells must be opaque.
                                    const bg = on ? 'bg-violet-50 dark:bg-muted' : locked ? 'bg-background' : 'bg-card';
                                    const cell = `${bg} border-b border-border`;
                                    const attention = attentionOf(row, tab);
                                    return (
                                        <tr key={row.row_id}
                                            data-testid="lm-row"
                                            data-attention={attention || undefined}
                                            onClick={() => !locked && onToggleRow(row.row_id)}
                                            className={locked ? 'opacity-50 cursor-default' : 'cursor-pointer'}
                                            data-selected={on || undefined}>
                                            <td className={`py-[11px] pl-4 ${CHECK_COL} ${PIN_CHECK} z-10 ${cell} ${attention ? ATTENTION_EDGE[attention] : ''}`}
                                                title={attention === 'error' ? 'Commit failed: open the record' : attention ? 'Needs review' : undefined}>
                                                <Checkbox checked={on} disabled={locked} onChange={() => onToggleRow(row.row_id)}
                                                          label={`Select ${displayName(row)}`} />
                                            </td>
                                            {columns.map((col, i) => (
                                                <td key={col.id} title={plainCellText(col, row)}
                                                    className={`px-3.5 py-[11px] text-[13px] text-foreground max-w-[320px] truncate ${cell} ${pinFirst && i === 0 ? `${PIN_NAME} z-10` : ''}`}>
                                                    <Cell column={col} row={row} tab={tab} onOpen={onOpenRow} />
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        );
                    })}
                </table>
                {body}
                {loading && !rows.length && !body && (
                    <div className="py-14 text-center text-[13px] text-muted-foreground" role="status">Loading…</div>
                )}
            </div>
            {!body && (
                <div className="shrink-0">
                    <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize}
                                     onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}
                                     pageSizeOptions={PAGE_SIZE_OPTIONS} alwaysShow disabled={loading} />
                </div>
            )}
        </div>
    );
}
